import { useState, useEffect } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { JourneyApi } from '../components/PlateViewRenderer'

export type JourneyTriggerStatus = 'idle' | 'pending' | 'running' | 'completed' | 'failed'

// Shared trigger+poll logic for the EnergyOS bespoke dashboard's several action points
// (Emergency Stop, Charge Now, Add Event) — same trigger/poll shape as PlateViewRenderer's
// internal RowActionJourney, just as a hook instead of a mounted component, since these actions
// live inside custom visual widgets (a device icon, a modal) rather than a generic row/button.
export function useJourneyTrigger(journeyApi: JourneyApi) {
  const [journeyId, setJourneyId] = useState<string | null>(null)
  const [status, setStatus] = useState<JourneyTriggerStatus>('idle')
  const [error, setError] = useState<string | null>(null)

  const trigger = useMutation({
    mutationFn: (vars: { preparationId: string; payload?: Record<string, string> }) =>
      journeyApi.trigger(vars.preparationId, vars.payload),
    onSuccess: (res) => {
      setJourneyId(res.data.id)
      setStatus(res.data.status as JourneyTriggerStatus)
    },
    onError: (err: any) => {
      setError(err?.response?.data?.error ?? err?.message ?? 'Failed to trigger')
      setStatus('failed')
    },
  })

  const { data: polled } = useQuery({
    queryKey: ['eos-dashboard-journey-poll', journeyId],
    queryFn: () => journeyApi.get(journeyId!).then(r => r.data),
    enabled: !!journeyId && (status === 'pending' || status === 'running'),
    refetchInterval: 800,
    retry: false,
  })

  useEffect(() => {
    if (polled && polled.status !== 'pending') setStatus(polled.status as JourneyTriggerStatus)
  }, [polled])

  const run = (preparationId: string, payload?: Record<string, string>) => {
    setError(null)
    trigger.mutate({ preparationId, payload })
  }

  const reset = () => {
    setJourneyId(null)
    setStatus('idle')
    setError(null)
  }

  return { run, status, error, reset, isBusy: status === 'pending' || status === 'running' }
}
